import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * White Classic Socks Add-to-cart handler (2026-09-11, redesigned to sit
 * inside the normal "You may also like" rail rather than a separate promo
 * block — see cart-recommendations.liquid for that row's markup/styling and
 * why its Add/"In bag" state is driven entirely by server-rendered
 * `cart.items`, never client-side JS state).
 *
 * This file's ONLY job is the ADD click: a single, guarded, completely
 * ordinary `/cart/add.js` call for exactly one unit of the authoritative
 * White Classic Socks variant — no properties, so this can NEVER become a
 * private-BOGO gift line (see brand-rep-private-bogo-gift.js's own removal
 * note — that auto-add system was deliberately deleted; this file must not
 * reintroduce anything like it). After a successful add, it dispatches the
 * same CartLinesUpdateEvent + `detail.sections` refresh convention already
 * proven in brand-rep-welcome-gift.js/brand-rep-private-bogo-gift.js, which
 * the theme's own cart-items-component/cart-drawer-component/cart-icon
 * already listen for — so the promo's Liquid re-renders with the correct
 * "In bag" state, the cart line list updates, and the header count updates,
 * all from ONE dispatch, with no page reload and no manual DOM patching
 * here.
 *
 * DUPLICATE PROTECTION: a module-scoped `addInFlight` flag ignores any
 * click received while a request is already in flight, and the clicked
 * button is disabled immediately and — on success — left disabled rather
 * than re-enabled, so even a rapid double-tap in the brief window before
 * the section refresh actually swaps this button out for the "In bag"
 * state can never fire a second add. It is only re-enabled if the add
 * itself failed, so the customer can retry.
 */

// Remediation 2026-10-04: the variant ID is no longer duplicated here. The
// Add button carries it as data-variant-id, rendered by
// snippets/cart-recommendations.liquid from the single source of truth
// (snippets/offer-white-sock-variant-id.liquid). The button only renders
// once Liquid has matched that exact variant, so the value is always set.
function variantIdFor(button) {
  const id = Number(button && button.dataset ? button.dataset.variantId : NaN);
  return Number.isInteger(id) && id > 0 ? id : null;
}

let addInFlight = false;
// After a successful add the row turns into the tote row in the same spot; taps in the next moment
// belong to the socks tap (a double tap), so they are ignored rather than adding the tote.
const AFTER_ADD_PAUSE_MS = 1500;
let ignoreTapsUntil = 0;

// Owner 2026-10-21: also checked against the live cart before every add, so a pair already in the
// bag (look page, another tab, an earlier tap) is never added a second time; only one pair is free.
function cartHasVariant(cart, variantId) {
  return Boolean(cart && Array.isArray(cart.items) && cart.items.some((item) => item.variant_id === variantId));
}

async function fetchSections(ids) {
  if (!ids.length) return {};
  const url = `${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`;
  const response = await fetch(url, { credentials: 'same-origin' });
  return response.ok ? response.json() : {};
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

async function dispatchRefresh(result) {
  const freshCart = await fetchCart();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ id: '', quantity: 1 }],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart),
        detail: {
          sections: result.sections,
          items: freshCart.items,
          itemCount: freshCart.item_count,
          source: 'cart-socks-promo',
          didError: false,
        },
      }),
    })
  );
}

async function addSocks(button) {
  if (addInFlight) return;
  const variantId = variantIdFor(button);
  if (!variantId) return; // never guess a variant
  addInFlight = true;
  button.disabled = true;

  try {
    const sections = cartSectionIds();
    if (cartHasVariant(await fetchCart(), variantId)) {
      // Already in the bag: refresh the rows instead of adding a second unit.
      await dispatchRefresh({ sections: await fetchSections(sections) });
      ignoreTapsUntil = Date.now() + AFTER_ADD_PAUSE_MS;
      addInFlight = false;
      return;
    }
    const body = JSON.stringify({
      // Deliberately NO `properties` — an ordinary merchandise line, never
      // a private-BOGO (or any other) gift-tracked line.
      items: [{ id: variantId, quantity: 1 }],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status) {
      document.dispatchEvent(
        new CartErrorEvent({
          // i18n 2026-09-29: fallback text comes from the button's data-error-message (Liquid | t);
          // the original English literal remains the last-resort fallback.
          error:
            (data && (data.description || data.message)) ||
            button.dataset.errorMessage ||
            'Could not add White Classic Socks. Please try again.',
          code: 'INVALID',
        })
      );
      addInFlight = false;
      if (button.isConnected) button.disabled = false;
      return;
    }

    await dispatchRefresh(data);
    ignoreTapsUntil = Date.now() + AFTER_ADD_PAUSE_MS;
    // Success: leave disabled — the refresh this triggers replaces this
    // button with the "In bag" state; never re-enable it, so a stray
    // double-click can never queue a second add.
    addInFlight = false;
  } catch (error) {
    console.error('[cart-socks-promo] add failed', error);
    addInFlight = false;
    if (button.isConnected) button.disabled = false;
  }
}

/**
 * Invitation note on the socks row (owner 2026-10-21): "free once you complete your set". Liquid
 * shows it for a logged-in customer whose private offer is active; this reveals it when this
 * browser session holds her invitation code (set by brand-rep.js on her signup success, or by
 * brand-rep-cart-assistant.js from her email link). Text only; it never touches the cart.
 */
const CONTEXT_KEY = 'ancartaBrandRepContext';

function sessionHasInvitation() {
  try {
    const context = JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || 'null');
    return Boolean(context && typeof context.code === 'string' && context.code);
  } catch (error) {
    return false;
  }
}

class CartSocksInviteNote extends HTMLElement {
  connectedCallback() {
    if (this.dataset.inviteKnown !== 'true' && sessionHasInvitation()) this.hidden = false;
  }
}

if (!customElements.get('cart-socks-invite-note')) {
  customElements.define('cart-socks-invite-note', CartSocksInviteNote);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-socks-promo-add]');
  if (!button) return;
  event.preventDefault();
  if (Date.now() < ignoreTapsUntil) return;
  addSocks(button);
});
